import React, { useState } from 'react';
import { Code, Copy, CheckCircle, ExternalLink, MessageCircle, Zap, Server, Key } from 'lucide-react';

export function A2ADocumentation() {
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  const copyToClipboard = (text: string, section: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const messageFormatExample = `{
  "sender": {
    "agent_id": "550e8400-e29b-41d4-a716-446655440000",
    "name": "Data Analysis Agent"
  },
  "recipient": {
    "agent_id": "550e8400-e29b-41d4-a716-446655440001",
    "name": "Visualization Agent"
  },
  "message_type": "request",
  "content": {
    "action": "create_visualization",
    "data": [
      {"category": "A", "value": 10},
      {"category": "B", "value": 15},
      {"category": "C", "value": 7}
    ],
    "visualization_type": "bar_chart",
    "title": "Sample Data Visualization"
  },
  "metadata": {
    "conversation_id": "conv-123456",
    "request_id": "req-abcdef",
    "timestamp": "2025-06-29T12:34:56Z",
    "priority": "normal",
    "capabilities": ["data_visualization", "image_generation"]
  }
}`;

  const registrationExample = `// Register your agent with the A2A protocol
const response = await fetch(
  \`\${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-protocol-handler/register\`,
  {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${import.meta.env.VITE_SUPABASE_ANON_KEY}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      agent_id: "your-agent-id",
      endpoint: "https://your-agent-endpoint.com/a2a",
      capabilities: ["text_generation", "data_analysis", "question_answering"]
    })
  }
);

const data = await response.json();
// Store the A2A key securely
const a2aKey = data.a2a_key;`;

  const sendMessageExample = `// Send a message to another agent
const response = await fetch(
  \`\${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-protocol-handler/send\`,
  {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${import.meta.env.VITE_SUPABASE_ANON_KEY}\`,
      'Content-Type': 'application/json',
      'X-A2A-Key': 'your-a2a-key'
    },
    body: JSON.stringify({
      sender: {
        agent_id: "your-agent-id",
        name: "Your Agent Name"
      },
      recipient: {
        agent_id: "recipient-agent-id"
      },
      message_type: "request",
      content: {
        // Your message content here
        action: "process_data",
        data: { /* ... */ }
      }
    })
  }
);

const result = await response.json();`;

  const discoverAgentsExample = `// Discover agents with specific capabilities
const response = await fetch(
  \`\${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-protocol-handler/discover\`,
  {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${import.meta.env.VITE_SUPABASE_ANON_KEY}\`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      capabilities: ["text_generation", "data_analysis"]
    })
  }
);

const { agents } = await response.json();
// agents is an array of available agents with the requested capabilities`;

  const receiveMessageExample = `// Example Express.js endpoint to receive A2A messages
app.post('/a2a', (req, res) => {
  const message = req.body;
  
  // Validate the message format
  if (!message.sender || !message.content) {
    return res.status(400).json({
      success: false,
      error: 'Invalid message format'
    });
  }
  
  // Process the message based on type
  if (message.message_type === 'request') {
    // Handle the request
    const result = processRequest(message.content);
    
    // Send response
    return res.json({
      success: true,
      sender: {
        agent_id: "your-agent-id",
        name: "Your Agent Name"
      },
      recipient: message.sender,
      message_type: "response",
      content: result,
      metadata: {
        request_id: message.metadata?.request_id,
        timestamp: new Date().toISOString()
      }
    });
  }
  
  // Handle other message types
  return res.json({ success: true });
});

function processRequest(content) {
  // Your agent's logic to process the request
  return {
    status: "success",
    result: { /* ... */ }
  };
}`;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-8 h-8" />
          <div>
            <h2 className="text-2xl font-bold">A2A Protocol Documentation</h2>
            <p className="opacity-90">Agent-to-Agent Communication Standard</p>
          </div>
        </div>
      </div>
      
      <div className="p-6 space-y-8">
        {/* Introduction */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Introduction</h3>
          <p className="text-gray-700 mb-4">
            The Agent-to-Agent (A2A) protocol is a standardized communication framework that enables AI agents to interact with each other programmatically. This protocol facilitates complex multi-agent systems where specialized agents can collaborate to solve problems.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-700 mb-2">
              <Zap className="w-5 h-5" />
              <span className="font-medium">Key Features</span>
            </div>
            <ul className="text-blue-700 text-sm space-y-1">
              <li>• Standardized message format for consistent communication</li>
              <li>• Secure authentication with API keys</li>
              <li>• Agent discovery mechanism for dynamic collaboration</li>
              <li>• Rate limiting to prevent abuse</li>
              <li>• Support for various message types (requests, responses, notifications)</li>
            </ul>
          </div>
        </section>
        
        {/* Message Format */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Message Format</h3>
          <p className="text-gray-700 mb-4">
            A2A messages follow a standard JSON format that includes sender and recipient information, message type, content payload, and optional metadata.
          </p>
          <div className="relative">
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-green-400 text-sm">{messageFormatExample}</pre>
            </div>
            <button
              onClick={() => copyToClipboard(messageFormatExample, 'message-format')}
              className="absolute top-3 right-3 p-2 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors"
              title="Copy to clipboard"
            >
              {copiedSection === 'message-format' ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Message Types</h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li><span className="font-medium">request:</span> A message requesting an action or information</li>
                <li><span className="font-medium">response:</span> A reply to a request</li>
                <li><span className="font-medium">error:</span> An error notification</li>
                <li><span className="font-medium">notification:</span> A one-way informational message</li>
              </ul>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Metadata Fields</h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li><span className="font-medium">conversation_id:</span> Groups related messages</li>
                <li><span className="font-medium">request_id:</span> Unique identifier for request-response pairs</li>
                <li><span className="font-medium">timestamp:</span> ISO-8601 formatted time</li>
                <li><span className="font-medium">ttl:</span> Time-to-live in seconds</li>
                <li><span className="font-medium">priority:</span> Message priority (low, normal, high)</li>
              </ul>
            </div>
          </div>
        </section>
        
        {/* Authentication */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Authentication</h3>
          <p className="text-gray-700 mb-4">
            A2A uses API key authentication. Each agent receives a unique A2A key upon registration, which must be included in the <code className="bg-gray-100 px-1 py-0.5 rounded">X-A2A-Key</code> header when sending messages.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-yellow-700 mb-2">
              <Key className="w-5 h-5" />
              <span className="font-medium">Security Best Practices</span>
            </div>
            <ul className="text-yellow-700 text-sm space-y-1">
              <li>• Store A2A keys securely, never expose them in client-side code</li>
              <li>• Rotate keys periodically for enhanced security</li>
              <li>• Use HTTPS for all A2A communication</li>
              <li>• Implement additional validation for sensitive operations</li>
            </ul>
          </div>
        </section>
        
        {/* Implementation */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Implementation Guide</h3>
          
          <div className="space-y-6">
            {/* Registration */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Server className="w-5 h-5 text-blue-600" />
                Step 1: Register Your Agent
              </h4>
              <p className="text-gray-700 mb-3">
                Register your agent with the A2A protocol handler to receive an A2A key and enable communication.
              </p>
              <div className="relative">
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-blue-400 text-sm">{registrationExample}</pre>
                </div>
                <button
                  onClick={() => copyToClipboard(registrationExample, 'registration')}
                  className="absolute top-3 right-3 p-2 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors"
                  title="Copy to clipboard"
                >
                  {copiedSection === 'registration' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            
            {/* Sending Messages */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-purple-600" />
                Step 2: Send Messages to Other Agents
              </h4>
              <p className="text-gray-700 mb-3">
                Use the A2A protocol handler to send messages to other agents.
              </p>
              <div className="relative">
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-purple-400 text-sm">{sendMessageExample}</pre>
                </div>
                <button
                  onClick={() => copyToClipboard(sendMessageExample, 'send-message')}
                  className="absolute top-3 right-3 p-2 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors"
                  title="Copy to clipboard"
                >
                  {copiedSection === 'send-message' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            
            {/* Discovering Agents */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-600" />
                Step 3: Discover Other Agents
              </h4>
              <p className="text-gray-700 mb-3">
                Find other agents with specific capabilities using the discovery endpoint.
              </p>
              <div className="relative">
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-green-400 text-sm">{discoverAgentsExample}</pre>
                </div>
                <button
                  onClick={() => copyToClipboard(discoverAgentsExample, 'discover')}
                  className="absolute top-3 right-3 p-2 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors"
                  title="Copy to clipboard"
                >
                  {copiedSection === 'discover' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            
            {/* Receiving Messages */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Server className="w-5 h-5 text-orange-600" />
                Step 4: Implement Message Receiving
              </h4>
              <p className="text-gray-700 mb-3">
                Create an endpoint in your agent to receive and process A2A messages.
              </p>
              <div className="relative">
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-orange-400 text-sm">{receiveMessageExample}</pre>
                </div>
                <button
                  onClick={() => copyToClipboard(receiveMessageExample, 'receive')}
                  className="absolute top-3 right-3 p-2 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors"
                  title="Copy to clipboard"
                >
                  {copiedSection === 'receive' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>
        
        {/* Rate Limiting */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Rate Limiting</h3>
          <p className="text-gray-700 mb-4">
            To ensure fair usage and system stability, the A2A protocol implements rate limiting based on agent tier:
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requests</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Default</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">60</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1 minute</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Free tier</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Premium</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">300</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">1 minute</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Paid agents</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        
        {/* Additional Resources */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Additional Resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a 
              href="https://github.com/google-ai/agent-to-agent"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="p-2 bg-blue-100 rounded-lg">
                <ExternalLink className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="font-medium text-gray-900">Google A2A Research</div>
                <div className="text-sm text-gray-600">Official Google research on agent-to-agent communication</div>
              </div>
            </a>
            <a 
              href="https://github.com/langchain-ai/langchain"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="p-2 bg-green-100 rounded-lg">
                <Code className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="font-medium text-gray-900">LangChain Agents</div>
                <div className="text-sm text-gray-600">Framework for building agent systems</div>
              </div>
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}